import { CloseCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { Result, Typography } from 'antd';
import React, { useContext } from 'react';
import { useRoutes } from 'react-router-dom';
import { AppContext } from './AppContext';
import Error404 from './Error404';
import HistoryRouter from './HistoryRouter';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import App from './app/App';
import {
  ERROR_PAGE_SUBTITLE,
  ERROR_PAGE_TITLE,
  ROUTES,
} from './common/constants';
import LoaderComponent from './components/LoaderComponent';
import MaintenancePage from './components/MaintenancePage';
import PreventNavigationProvider from './context/PreventNavigationProvider';
import history from './historyData';
import Login from './modules/auth/Login';
import Logout from './modules/auth/Logout';
import OnBoarding from './modules/profile/OnBoarding';
import { GET_CONFIG } from './modules/profile/graphql/Queries';
import AddQuiz from './modules/quiz/AddQuiz';
import Details from './modules/quiz/Details';
import EditQuiz from './modules/quiz/EditQuiz';
import QuizList from './modules/quiz/QuizList';
import Subscription from './modules/subscription/Subscription';

const { Paragraph } = Typography;

const MyFallbackComponent = ({ error, componentStack }) => (
  <Result
    status="error"
    title={ERROR_PAGE_TITLE}
    subTitle={ERROR_PAGE_SUBTITLE}
  >
    <div className="desc">
      <Paragraph>
        <Typography.Title level={4}> Error:</Typography.Title>
      </Paragraph>
      <Paragraph>
        <CloseCircleOutlined className="site-result-demo-error-icon" /> Your
        {error?.message?.toString()}
      </Paragraph>
      <Paragraph>
        <Typography.Title level={4}> Stacktrace:</Typography.Title>
      </Paragraph>
      <Paragraph>
        <CloseCircleOutlined className="site-result-demo-error-icon" /> Your
        {componentStack}
      </Paragraph>
    </div>
  </Result>
);

const RoutesCollection = () => {
  const AUTH_MODULES = [
    {
      path: ROUTES?.LOGIN,
      element: <PublicRoute />,
      children: [{ path: ROUTES?.LOGIN, element: <Login /> }],
    },
    {
      path: ROUTES?.LOGOUT,
      element: <PrivateRoute />,
      children: [{ path: ROUTES?.LOGOUT, element: <Logout /> }],
    },
  ];

  const QUIZ_MODULES = [
    {
      path: ROUTES?.MAIN,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES?.MAIN,
          element: <App />,
          children: [
            { path: ROUTES?.MAIN, element: <QuizList /> },
            { path: ROUTES?.ADD_QUIZ, element: <AddQuiz /> },
            { path: ROUTES?.EDIT_QUIZ, element: <EditQuiz /> },
            { path: ROUTES?.DETAILS, element: <Details /> },
          ],
        },
      ],
    },
  ];

  const USER_MODULES = [
    {
      path: ROUTES?.MAIN,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES?.MAIN,
          element: <App />,
          children: [
            {
              path: ROUTES.ONBOARDING,
              element: <OnBoarding />,
            },
          ],
        },
      ],
    },
  ];

  const SUBSCRIPTION_MODULES = [
    {
      path: ROUTES?.MAIN,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES?.MAIN,
          element: <App />,
          children: [
            {
              path: ROUTES.SUBSCRIPTION,
              element: <Subscription />,
            },
          ],
        },
      ],
    },
  ];

  const OTHER_MODULES = [
    {
      path: ROUTES?.MAIN,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES?.MAIN,
          element: <App />,
          children: [
            {
              path: '*',
              element: <Error404 />,
            },
          ],
        },
      ],
    },
  ];

  const element = useRoutes([
    ...AUTH_MODULES,
    ...QUIZ_MODULES,
    ...USER_MODULES,
    ...SUBSCRIPTION_MODULES,
    ...OTHER_MODULES,
  ]);
  return element;
};

const RoutesWrapper = () => {
  const loading = false;
  const { getCurrentUser, initializeAuth, getToken } = useContext(AppContext);
  const userData = getCurrentUser();
  const token = getToken();
  const accessToken = token;
  const refreshToken = userData?.refreshToken;

  useQuery(GET_CONFIG, {
    skip: !accessToken,
    onCompleted: ({ getConfig }) => {
      initializeAuth(
        accessToken,
        { ...userData, ...getConfig?.config },
        refreshToken,
      );
    },
    onError() {},
  });

  // use this variable from envs so that we can able to run maintenance page on runtime.
  const maintenance = process.env.REACT_APP_MAINTENANCE_ENABLE;

  if (loading) return <LoaderComponent />;

  return (
    <Sentry.ErrorBoundary fallback={MyFallbackComponent}>
      <HistoryRouter history={history}>
        <PreventNavigationProvider>
          {maintenance === 'true' ? <MaintenancePage /> : <RoutesCollection />}
        </PreventNavigationProvider>
      </HistoryRouter>
    </Sentry.ErrorBoundary>
  );
};
export default RoutesWrapper;
